import {
  acidConcentrationFromTitre,
  phenolphthaleinAppearance,
  selectGuidedCalculationTitres,
  guidedTitrationTrialIssue,
  titrationPh,
  makeTitrationTrial,
} from "./models";
import {
  recordCurrentTitrationTrial,
  refillForNextTitrationTrial,
  type TitrationSessionState,
} from "./session";

export enum TitrationGuidedStep {
  INTRO = "intro",
  CLEAR_BUBBLE = "clear-bubble",
  ADD_INDICATOR = "add-indicator",
  OPEN_TAP = "open-tap",
  STOP_TAP = "stop-tap",
  SWIRL = "swirl",
  RECORD_TITRE = "record-titre",
  REFILL = "refill",
  REPEAT_READY = "repeat-ready",
  CALCULATE = "calculate",
}

export type TitrationGuidanceTarget =
  | "tip"
  | "flask"
  | "tap"
  | "reading"
  | null;

export type TitrationCheckpoint = {
  label: string;
  session: TitrationSessionState;
  step: TitrationGuidedStep;
};

export type TitrationGuidedState = {
  session: TitrationSessionState;
  step: TitrationGuidedStep;
  checkpoints: readonly TitrationCheckpoint[];
  reviewIndex: number | null;
  calculationOpen: boolean;
};

export type TitrationGuidedAction =
  | { type: "START_PRACTICAL" }
  | { type: "CLEAR_BUBBLE" }
  | { type: "ADD_INDICATOR" }
  | { type: "OPEN_TAP" }
  | { type: "FLOW_TICK"; deltaMl: number }
  | { type: "SET_FLOW"; fraction: number }
  | { type: "ONE_DROP" }
  | { type: "STOP_TAP" }
  | { type: "SWIRL" }
  | { type: "RECORD_TITRE" }
  | { type: "REFILL" }
  | { type: "START_NEXT_TRIAL" }
  | { type: "SHOW_CALCULATION" }
  | { type: "BACK_REVIEW" }
  | { type: "FORWARD_REVIEW" }
  | { type: "RETURN_TO_CURRENT" }
  | { type: "UNDO" }
  | { type: "RECONCILE"; session: TitrationSessionState };

export type TitrationGuidedStepDefinition = {
  id: TitrationGuidedStep;
  ordinal: number | null;
  total: number;
  eyebrow: string;
  title: string;
  body: string;
  supportingBody?: string;
  primaryLabel: string | null;
  primaryAction: TitrationGuidedAction | null;
  target: TitrationGuidanceTarget;
};

export const GUIDED_TAP_OPEN_FRACTION = 0.65;
export const TITRATION_GUIDED_TOTAL_STEPS = 7;

export function titrationTapSettingLabel(fraction: number) {
  if (fraction <= 0.05) return "Dropwise";
  if (fraction <= 0.25) return "Gentle flow";
  return "Faster flow";
}

export function selectedTitrationFlow(session: TitrationSessionState) {
  const selected = session.preferredTapOpenFraction;
  return selected !== undefined && Number.isFinite(selected)
    ? Math.max(0.01, Math.min(1, selected))
    : GUIDED_TAP_OPEN_FRACTION;
}

function checkpoint(
  state: TitrationGuidedState,
  label: string
): readonly TitrationCheckpoint[] {
  return [
    ...state.checkpoints,
    {
      label,
      session: state.session,
      step: state.step,
    },
  ];
}

function endpointVisible(session: TitrationSessionState) {
  return phenolphthaleinAppearance(
    titrationPh(session.dispensedMl, session.airBubblePresent)
  ).persistentEndpoint;
}

export function deriveInitialTitrationStep(
  session: TitrationSessionState
): TitrationGuidedStep {
  if (selectGuidedCalculationTitres(session.trials)) {
    return TitrationGuidedStep.CALCULATE;
  }
  if (session.trials.length > 0) {
    return TitrationGuidedStep.REPEAT_READY;
  }
  return TitrationGuidedStep.INTRO;
}

export function createInitialTitrationGuidedState(
  session: TitrationSessionState
): TitrationGuidedState {
  return {
    session,
    step: deriveInitialTitrationStep(session),
    checkpoints: [],
    reviewIndex: null,
    calculationOpen: false,
  };
}

function isStepCompatible(
  step: TitrationGuidedStep,
  session: TitrationSessionState
) {
  const concordant = selectGuidedCalculationTitres(session.trials);
  switch (step) {
    case TitrationGuidedStep.INTRO:
      return (
        session.trials.length === 0 &&
        session.dispensedMl === 0 &&
        session.airBubblePresent &&
        !session.indicatorAdded
      );
    case TitrationGuidedStep.CLEAR_BUBBLE:
      return session.airBubblePresent && session.dispensedMl === 0;
    case TitrationGuidedStep.ADD_INDICATOR:
      return !session.indicatorAdded && session.tapOpenFraction === 0;
    case TitrationGuidedStep.OPEN_TAP:
      return session.indicatorAdded && session.tapOpenFraction === 0;
    case TitrationGuidedStep.STOP_TAP:
      return session.tapOpenFraction > 0;
    case TitrationGuidedStep.SWIRL:
      return session.dispensedMl > 0 && !session.swirled;
    case TitrationGuidedStep.RECORD_TITRE:
      return (
        session.dispensedMl >= 1 && session.swirled && endpointVisible(session)
      );
    case TitrationGuidedStep.REFILL:
      return session.trials.length > 0 && session.dispensedMl >= 1;
    case TitrationGuidedStep.REPEAT_READY:
      return (
        session.trials.length > 0 &&
        session.dispensedMl === 0 &&
        !session.indicatorAdded &&
        !concordant
      );
    case TitrationGuidedStep.CALCULATE:
      return Boolean(concordant);
  }
}

export function reconcileGuidedStep(
  currentStep: TitrationGuidedStep,
  session: TitrationSessionState
): TitrationGuidedStep {
  if (selectGuidedCalculationTitres(session.trials)) {
    return TitrationGuidedStep.CALCULATE;
  }
  if (isStepCompatible(currentStep, session)) return currentStep;
  if (session.airBubblePresent) return TitrationGuidedStep.CLEAR_BUBBLE;
  if (!session.indicatorAdded) {
    return session.trials.length
      ? TitrationGuidedStep.REPEAT_READY
      : TitrationGuidedStep.ADD_INDICATOR;
  }
  if (session.tapOpenFraction > 0) return TitrationGuidedStep.STOP_TAP;
  if (session.dispensedMl <= 0) return TitrationGuidedStep.OPEN_TAP;
  if (!session.swirled) return TitrationGuidedStep.SWIRL;
  return endpointVisible(session)
    ? TitrationGuidedStep.RECORD_TITRE
    : TitrationGuidedStep.OPEN_TAP;
}

export function displayedGuidedSnapshot(state: TitrationGuidedState) {
  if (state.reviewIndex === null) {
    return {
      session: state.session,
      step: state.step,
      reviewMode: false,
    };
  }
  const checkpointState = state.checkpoints[state.reviewIndex];
  if (!checkpointState) {
    return {
      session: state.session,
      step: state.step,
      reviewMode: false,
    };
  }
  return {
    session: checkpointState.session,
    step: checkpointState.step,
    reviewMode: true,
  };
}

export function titrationGuidedReducer(
  state: TitrationGuidedState,
  action: TitrationGuidedAction
): TitrationGuidedState {
  switch (action.type) {
    case "START_PRACTICAL": {
      if (state.step !== TitrationGuidedStep.INTRO) return state;
      const nextStep = state.session.airBubblePresent
        ? TitrationGuidedStep.CLEAR_BUBBLE
        : state.session.indicatorAdded
        ? TitrationGuidedStep.OPEN_TAP
        : TitrationGuidedStep.ADD_INDICATOR;
      return {
        ...state,
        step: nextStep,
        checkpoints: checkpoint(state, "start practical"),
        reviewIndex: null,
      };
    }
    case "CLEAR_BUBBLE": {
      if (
        state.step !== TitrationGuidedStep.CLEAR_BUBBLE ||
        !state.session.airBubblePresent ||
        state.session.dispensedMl > 0
      ) {
        return state;
      }
      return {
        ...state,
        session: { ...state.session, airBubblePresent: false },
        step: TitrationGuidedStep.ADD_INDICATOR,
        checkpoints: checkpoint(state, "clear bubble"),
        reviewIndex: null,
      };
    }
    case "ADD_INDICATOR": {
      if (
        state.step !== TitrationGuidedStep.ADD_INDICATOR ||
        state.session.indicatorAdded
      ) {
        return state;
      }
      return {
        ...state,
        session: { ...state.session, indicatorAdded: true },
        step: TitrationGuidedStep.OPEN_TAP,
        checkpoints: checkpoint(state, "add indicator"),
        reviewIndex: null,
      };
    }
    case "OPEN_TAP": {
      if (
        state.step !== TitrationGuidedStep.OPEN_TAP ||
        !state.session.indicatorAdded
      ) {
        return state;
      }
      return {
        ...state,
        session: {
          ...state.session,
          tapOpenFraction: selectedTitrationFlow(state.session),
          swirled: false,
        },
        step: TitrationGuidedStep.STOP_TAP,
        checkpoints: checkpoint(state, "add NaOH"),
        reviewIndex: null,
      };
    }
    case "SET_FLOW": {
      if (
        ![TitrationGuidedStep.OPEN_TAP, TitrationGuidedStep.STOP_TAP].includes(
          state.step
        ) ||
        !Number.isFinite(action.fraction)
      )
        return state;
      return {
        ...state,
        session: {
          ...state.session,
          preferredTapOpenFraction: Math.max(
            0.01,
            Math.min(1, action.fraction)
          ),
          tapOpenFraction:
            state.session.tapOpenFraction > 0
              ? Math.max(0.01, Math.min(1, action.fraction))
              : 0,
        },
      };
    }
    case "ONE_DROP": {
      if (
        state.step !== TitrationGuidedStep.OPEN_TAP ||
        !state.session.indicatorAdded ||
        state.session.tapOpenFraction > 0 ||
        state.session.dispensedMl >= 24.8
      )
        return state;
      return {
        ...state,
        session: {
          ...state.session,
          dispensedMl: Number(
            Math.min(24.8, state.session.dispensedMl + 0.05).toFixed(3)
          ),
          tapOpenFraction: 0,
          swirled: false,
        },
        step: TitrationGuidedStep.SWIRL,
        checkpoints: checkpoint(state, "one drop"),
        reviewIndex: null,
      };
    }
    case "FLOW_TICK": {
      if (
        state.step !== TitrationGuidedStep.STOP_TAP ||
        state.session.tapOpenFraction <= 0 ||
        !Number.isFinite(action.deltaMl) ||
        action.deltaMl <= 0
      ) {
        return state;
      }
      const dispensedMl = Math.min(
        24.8,
        state.session.dispensedMl + action.deltaMl
      );
      return {
        ...state,
        session: {
          ...state.session,
          dispensedMl: Number(dispensedMl.toFixed(3)),
          tapOpenFraction:
            dispensedMl >= 24.8 ? 0 : state.session.tapOpenFraction,
          swirled: false,
        },
      };
    }
    case "STOP_TAP": {
      if (state.step !== TitrationGuidedStep.STOP_TAP) return state;
      return {
        ...state,
        session: { ...state.session, tapOpenFraction: 0 },
        step:
          state.session.dispensedMl > 0
            ? TitrationGuidedStep.SWIRL
            : TitrationGuidedStep.OPEN_TAP,
        reviewIndex: null,
      };
    }
    case "SWIRL": {
      if (
        state.step !== TitrationGuidedStep.SWIRL ||
        state.session.dispensedMl <= 0 ||
        !state.session.indicatorAdded
      ) {
        return state;
      }
      const session = { ...state.session, swirled: true };
      return {
        ...state,
        session,
        step: endpointVisible(session)
          ? TitrationGuidedStep.RECORD_TITRE
          : TitrationGuidedStep.OPEN_TAP,
        checkpoints: checkpoint(state, "swirl flask"),
        reviewIndex: null,
      };
    }
    case "RECORD_TITRE": {
      if (state.step !== TitrationGuidedStep.RECORD_TITRE) return state;
      const session = recordCurrentTitrationTrial(state.session);
      if (session === state.session) return state;
      return {
        ...state,
        session,
        step: selectGuidedCalculationTitres(session.trials)
          ? TitrationGuidedStep.CALCULATE
          : TitrationGuidedStep.REFILL,
        checkpoints: checkpoint(state, "record titre"),
        reviewIndex: null,
      };
    }
    case "REFILL": {
      if (state.step !== TitrationGuidedStep.REFILL) return state;
      return {
        ...state,
        session: refillForNextTitrationTrial(state.session),
        step: TitrationGuidedStep.REPEAT_READY,
        checkpoints: checkpoint(state, "refill flask"),
        reviewIndex: null,
      };
    }
    case "START_NEXT_TRIAL": {
      if (state.step !== TitrationGuidedStep.REPEAT_READY) return state;
      return {
        ...state,
        step: TitrationGuidedStep.ADD_INDICATOR,
        checkpoints: checkpoint(state, "start next trial"),
        reviewIndex: null,
      };
    }
    case "SHOW_CALCULATION": {
      if (state.step !== TitrationGuidedStep.CALCULATE) return state;
      return { ...state, calculationOpen: true, reviewIndex: null };
    }
    case "BACK_REVIEW": {
      if (!state.checkpoints.length) return state;
      return {
        ...state,
        reviewIndex:
          state.reviewIndex === null
            ? state.checkpoints.length - 1
            : Math.max(0, state.reviewIndex - 1),
      };
    }
    case "FORWARD_REVIEW": {
      if (state.reviewIndex === null) return state;
      const nextIndex = state.reviewIndex + 1;
      return {
        ...state,
        reviewIndex: nextIndex >= state.checkpoints.length ? null : nextIndex,
      };
    }
    case "RETURN_TO_CURRENT":
      return { ...state, reviewIndex: null };
    case "UNDO": {
      const previous = state.checkpoints[state.checkpoints.length - 1];
      if (!previous) return state;
      return {
        ...state,
        session: previous.session,
        step: previous.step,
        checkpoints: state.checkpoints.slice(0, -1),
        reviewIndex: null,
        calculationOpen: false,
      };
    }
    case "RECONCILE":
      return {
        ...state,
        session: action.session,
        step: reconcileGuidedStep(state.step, action.session),
        reviewIndex: null,
        calculationOpen: false,
      };
  }
}

function definition(
  id: TitrationGuidedStep,
  ordinal: number | null,
  eyebrow: string,
  title: string,
  body: string,
  primaryLabel: string | null,
  primaryAction: TitrationGuidedAction | null,
  target: TitrationGuidanceTarget,
  supportingBody?: string
): TitrationGuidedStepDefinition {
  return {
    id,
    ordinal,
    total: TITRATION_GUIDED_TOTAL_STEPS,
    eyebrow,
    title,
    body,
    supportingBody,
    primaryLabel,
    primaryAction,
    target,
  };
}

export function getTitrationGuidedStepDefinition(state: {
  session: TitrationSessionState;
  step: TitrationGuidedStep;
}): TitrationGuidedStepDefinition {
  const { session, step } = state;
  const trial =
    step === TitrationGuidedStep.REFILL
      ? session.trials.length
      : session.trials.length + 1;
  const lastTrial = session.trials.at(-1);
  const trialIssue = lastTrial ? guidedTitrationTrialIssue(lastTrial) : null;
  switch (step) {
    case TitrationGuidedStep.INTRO:
      return definition(
        step,
        null,
        "Guided practical",
        "Find the acid concentration",
        "Titrate unknown hydrochloric acid with 0.1000 M sodium hydroxide.",
        "Start practical",
        { type: "START_PRACTICAL" },
        null
      );
    case TitrationGuidedStep.CLEAR_BUBBLE:
      return definition(
        step,
        1,
        `Trial ${trial}`,
        "Clear the burette tip bubble",
        "Remove the air bubble from the burette tip so the volume reading is reliable.",
        "Clear bubble",
        { type: "CLEAR_BUBBLE" },
        "tip"
      );
    case TitrationGuidedStep.ADD_INDICATOR:
      return definition(
        step,
        2,
        `Trial ${trial}`,
        "Add indicator",
        "Add phenolphthalein to the flask so you can see the endpoint.",
        "Add indicator",
        { type: "ADD_INDICATOR" },
        "flask"
      );
    case TitrationGuidedStep.OPEN_TAP:
      return definition(
        step,
        3,
        `Trial ${trial}`,
        session.dispensedMl > 0
          ? "Add a little more sodium hydroxide"
          : "Add sodium hydroxide",
        session.dispensedMl > 0
          ? session.trials.length > 0 &&
            session.dispensedMl < session.trials[0].titreMl - 2
            ? "Continue bulk delivery, stopping to swirl and check the colour. Slow down well before your rough titre, then add dropwise."
            : "Choose dropwise flow or add one drop, then stop and swirl to check the colour."
          : "Set the tap for individual drops or faster delivery, then open it.",
        "Open tap",
        { type: "OPEN_TAP" },
        "tap",
        session.dispensedMl === 0
          ? "Use a rough trial to locate the colour change. On repeats, use faster flow for bulk delivery and slow down well before the rough titre."
          : undefined
      );
    case TitrationGuidedStep.STOP_TAP:
      return definition(
        step,
        4,
        `Trial ${trial}`,
        "Stop the flow",
        "Close the burette tap, then swirl and check the flask.",
        "Stop tap",
        { type: "STOP_TAP" },
        "tap"
      );
    case TitrationGuidedStep.SWIRL:
      return definition(
        step,
        5,
        `Trial ${trial}`,
        "Swirl the flask",
        "Mix the solution evenly before deciding whether more sodium hydroxide is needed.",
        "Swirl flask",
        { type: "SWIRL" },
        "flask"
      );
    case TitrationGuidedStep.RECORD_TITRE: {
      const currentIssue = guidedTitrationTrialIssue(
        makeTitrationTrial(
          session.dispensedMl,
          session.airBubblePresent,
          session.eyeLevel
        )
      );
      return definition(
        step,
        6,
        `Trial ${trial}`,
        currentIssue ? "Record this trial for review" : "Record your titre",
        currentIssue ??
          "The colour change persists after mixing. Record this reading before preparing another trial.",
        "Record titre",
        { type: "RECORD_TITRE" },
        "reading"
      );
    }
    case TitrationGuidedStep.REFILL:
      return definition(
        step,
        7,
        `Trial ${trial}`,
        trialIssue ? "Repeat this trial" : "Prepare the next trial",
        trialIssue ??
          "Refill the burette and prepare a fresh flask so you can obtain another titre.",
        "Refill",
        { type: "REFILL" },
        "flask"
      );
    case TitrationGuidedStep.REPEAT_READY:
      return definition(
        step,
        null,
        `Trial ${trial}`,
        "Do another trial",
        "Repeat until two endpoint readings are close enough to use. Trials with a technique error remain recorded but are excluded from this guided calculation.",
        "Start next trial",
        { type: "START_NEXT_TRIAL" },
        "flask"
      );
    case TitrationGuidedStep.CALCULATE:
      return definition(
        step,
        null,
        "Concordant titres ready",
        "Calculate the concentration",
        "You now have concordant titres. Use them to calculate the concentration of the unknown hydrochloric acid.",
        "Show calculation",
        { type: "SHOW_CALCULATION" },
        "reading"
      );
  }
}

export function getTitrationCalculation(session: TitrationSessionState) {
  const concordant = selectGuidedCalculationTitres(session.trials);
  if (!concordant) return null;
  return {
    ...concordant,
    concentrationM: acidConcentrationFromTitre(concordant.meanMl),
  };
}

export function targetLabelFor(target: TitrationGuidanceTarget) {
  switch (target) {
    case "tip":
      return "Burette tip";
    case "flask":
      return "Flask";
    case "tap":
      return "Tap";
    case "reading":
      return "Reading";
    case null:
      return null;
  }
}
